/**
 * Derived attention states (PRD §9.1). Never stored on a sample: always computed from its fields and
 * "now", so a flag can never be stale. Order = the samples workspace's Needs-attention buckets (§9.5).
 */
import type { SampleStatus, FsrMatch } from './lifecycle';

export type AttentionFlag =
  | 'check_fsr' | 'needs_ticket' | 'bottle_not_received' | 'not_shipped'
  | 'overdue_at_lab' | 'new_report' | 'action_required' | 'lost';

export const ATTENTION_ORDER: readonly AttentionFlag[] = [
  'check_fsr', 'needs_ticket', 'bottle_not_received', 'not_shipped',
  'overdue_at_lab', 'new_report', 'action_required', 'lost',
];

export interface AttentionInput {
  readonly status: SampleStatus;
  readonly fsrMatch: FsrMatch;
  readonly fsrAmbiguous: boolean;
  /** A person resolved the Check FSR item (PRD §9.6 "Check FSR items … are resolved here"). */
  readonly fsrReviewed: boolean;
  readonly serviceTicketId: string | null;
  readonly collectedAt: number | null;
  readonly loggedAt: number | null;
  readonly sentAt: number | null;
  /** When the sample was marked lost (null only if undated — then it stays visible). */
  readonly lostAt: number | null;
  readonly shipmentId: string | null;
  readonly actionCode: 'no_action' | 'monitor' | 'action_required' | 'unknown' | null;
  /**
   * A bottle created after the fact for a visit too old to act on (the FSR backfill). It stays a real,
   * matchable bottle but never raises 'bottle not received' and, once resulted, never raises 'new report'
   * (an action-required verdict still shows). Absent/false: a live bottle.
   */
  readonly historical?: boolean;
}

export interface AttentionThresholds {
  readonly bottleArrivalDays: number;
  readonly notShippedDays: number;
  readonly expectedTurnaroundDays: number;
  /** A lost sample leaves the attention list this long after it was marked lost (PRD §9.5). */
  readonly lostWindowDays: number;
}

export const DEFAULT_THRESHOLDS: AttentionThresholds = { bottleArrivalDays: 5, notShippedDays: 3, expectedTurnaroundDays: 14, lostWindowDays: 90 };

const DAY = 86_400_000;
const olderThan = (at: number | null, days: number, now: number): boolean => at !== null && now - at > days * DAY;

export function attentionFlags(s: AttentionInput, now: number, t: AttentionThresholds = DEFAULT_THRESHOLDS): AttentionFlag[] {
  if (s.status === 'closed' || s.status === 'cancelled') return [];
  if (s.status === 'lost') return olderThan(s.lostAt, t.lostWindowDays, now) ? [] : ['lost'];

  const flags = new Set<AttentionFlag>();
  if (!s.fsrReviewed && (s.fsrMatch === 'not_on_fsr' || s.fsrMatch === 'unit_mismatch' || s.fsrAmbiguous)) flags.add('check_fsr');
  if (s.status === 'logged' && s.serviceTicketId === null) flags.add('needs_ticket');
  if (s.status === 'expected' && !s.historical && olderThan(s.collectedAt, t.bottleArrivalDays, now)) flags.add('bottle_not_received');
  if (s.status === 'logged' && s.shipmentId === null && olderThan(s.loggedAt, t.notShippedDays, now)) flags.add('not_shipped');
  if (s.status === 'sent' && olderThan(s.sentAt, t.expectedTurnaroundDays, now)) flags.add('overdue_at_lab');
  if (s.status === 'resulted') {
    // A historical bottle (made after the fact for a visit the office already closed) never asks to be read as new.
    if (!s.historical) flags.add('new_report');
    if (s.actionCode === 'monitor' || s.actionCode === 'action_required') flags.add('action_required');
  }
  return [...flags].sort((a, b) => ATTENTION_ORDER.indexOf(a) - ATTENTION_ORDER.indexOf(b));
}

export function worstFlag(flags: readonly AttentionFlag[]): AttentionFlag | null {
  let best: AttentionFlag | null = null;
  for (const f of flags) if (best === null || ATTENTION_ORDER.indexOf(f) < ATTENTION_ORDER.indexOf(best)) best = f;
  return best;
}
